import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Rocket, Loader2 } from "lucide-react";

const BG = "https://images.unsplash.com/photo-1769837496184-32831581c55a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwzfHxjYXJnbyUyMHNoaXAlMjBwb3J0JTIwbmlnaHQlMjBkYXJrJTIwbW9vZHl8ZW58MHx8fHwxNzg1MjY5NDk5fDA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await login(email.trim().toLowerCase(), password);
      navigate("/app");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex bg-background">
      <div className="hidden lg:block relative flex-1">
        <img src={BG} alt="port" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-zinc-950/70" />
        <div className="relative z-10 h-full flex flex-col justify-between p-10">
          <div className="flex items-center gap-2">
            <Rocket className="h-6 w-6 text-blue-500" />
            <span className="font-heading font-bold tracking-tight text-lg text-white">ROKET</span>
          </div>
          <div>
            <h1 className="font-heading text-3xl font-semibold tracking-tight text-white max-w-md">
              EU Customs Classification & Declaration Console
            </h1>
            <p className="mt-3 text-zinc-400 text-sm max-w-md font-mono">
              TARIC classification · duty & compliance · ZZI eCustoms IE415B / IE515
            </p>
          </div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
          <div className="flex items-center gap-2 mb-8 lg:hidden">
            <Rocket className="h-6 w-6 text-blue-500" />
            <span className="font-heading font-bold text-lg">ROKET</span>
          </div>
          <h2 className="font-heading text-2xl font-semibold tracking-tight">Sign in</h2>
          <p className="text-sm text-zinc-500 mt-1 mb-6">Access the customs operations console.</p>

          {error && <div data-testid="login-error" className="mb-4 px-3 py-2 rounded-sm border border-red-500/40 bg-red-500/10 text-red-400 text-xs font-mono">{error}</div>}

          <label className="block text-[11px] uppercase tracking-wider text-zinc-500 mb-1">Email</label>
          <input data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            className="w-full mb-4 bg-zinc-900 border border-zinc-800 rounded-sm px-3 py-2 text-sm font-mono focus:border-blue-500 outline-none" />

          <label className="block text-[11px] uppercase tracking-wider text-zinc-500 mb-1">Password</label>
          <input data-testid="login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
            className="w-full mb-6 bg-zinc-900 border border-zinc-800 rounded-sm px-3 py-2 text-sm font-mono focus:border-blue-500 outline-none" />

          <button data-testid="login-submit" disabled={loading}
            className="w-full bg-blue-600 hover:bg-blue-500 disabled:opacity-60 text-white text-sm font-medium py-2.5 rounded-sm flex items-center justify-center gap-2 transition-colors duration-150">
            {loading && <Loader2 className="h-4 w-4 animate-spin" />} Sign in
          </button>

          <p className="mt-6 text-xs text-zinc-500 text-center">
            No account? <Link to="/register" data-testid="link-register" className="text-blue-400 hover:underline">Request access</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
